import { useState } from 'react';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import { BotaoFlutuante } from '@/components/layout/BotaoFlutuante';
import {
  Plus,
  X,
  RefreshCw,
  Pencil,
  Trash2,
  Lock,
  Unlock,
  Briefcase,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TableSkeleton } from '@/components/common/Skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { Selo } from '@/components/common/ListaMobile';
import { cn } from '@/lib/utils';
import { useJobs, useCreateJob, useUpdateJob, useDeleteJob } from '@/hooks/useCareers';
import { JOB_AREAS } from '@/api/endpoints/careers';
import type { Job, JobInput, JobStatus } from '@/api/endpoints/careers';

type FormState = {
  titulo: string;
  area: string;
  tipoContrato: string;
  local: string;
  resumo: string;
  descricao: string;
  responsabilidades: string;
  requisitos: string;
  processo: string;
  status: JobStatus;
  ordem: number;
};

const emptyForm: FormState = {
  titulo: '',
  area: JOB_AREAS[0],
  tipoContrato: '',
  local: '',
  resumo: '',
  descricao: '',
  responsabilidades: '',
  requisitos: '',
  processo: '',
  status: 'aberta',
  ordem: 0,
};

function jobToForm(job: Job): FormState {
  return {
    titulo: job.titulo,
    area: job.area,
    tipoContrato: job.tipoContrato,
    local: job.local,
    resumo: job.resumo,
    descricao: job.descricao,
    responsabilidades: (job.responsabilidades ?? []).join('\n'),
    requisitos: (job.requisitos ?? []).join('\n'),
    processo: job.processo,
    status: job.status,
    ordem: job.ordem,
  };
}

function formToInput(form: FormState): JobInput {
  return {
    titulo: form.titulo.trim(),
    area: form.area,
    tipoContrato: form.tipoContrato.trim(),
    local: form.local.trim(),
    resumo: form.resumo.trim(),
    descricao: form.descricao.trim(),
    responsabilidades: form.responsabilidades
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean),
    requisitos: form.requisitos
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean),
    processo: form.processo.trim(),
    status: form.status,
    ordem: form.ordem,
  };
}

const inputClass =
  'w-full rounded-lg border border-border bg-bg-elevated px-4 py-2 text-sm text-text-body placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';
const labelClass = 'mb-2 block text-sm font-medium text-text-title';

export function JobsTab() {
  const { data: jobs, isLoading, error } = useJobs();
  const createJobMutation = useCreateJob();
  const updateJobMutation = useUpdateJob();
  const deleteJobMutation = useDeleteJob();

  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const isSaving = createJobMutation.isPending || updateJobMutation.isPending;

  const openCreate = () => {
    setEditingJob(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (job: Job) => {
    setEditingJob(job);
    setForm(jobToForm(job));
    setShowModal(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setShowModal(false);
    setEditingJob(null);
  };

  const handleSave = async () => {
    if (!form.titulo.trim() || !form.tipoContrato.trim() || !form.local.trim()) {
      alert('Título, tipo de contrato e local são obrigatórios');
      return;
    }
    try {
      const payload = formToInput(form);
      if (editingJob) {
        await updateJobMutation.mutateAsync({ id: editingJob.id, data: payload });
      } else {
        await createJobMutation.mutateAsync(payload);
      }
      setShowModal(false);
      setEditingJob(null);
    } catch (err) {
      console.error('Erro ao salvar vaga:', err);
      alert('Erro ao salvar a vaga. Verifique os dados e tente novamente.');
    }
  };

  const handleToggleStatus = async (job: Job) => {
    try {
      await updateJobMutation.mutateAsync({
        id: job.id,
        data: { ...jobToFormInput(job), status: job.status === 'aberta' ? 'fechada' : 'aberta' },
      });
    } catch (err) {
      console.error('Erro ao atualizar status da vaga:', err);
      alert('Erro ao atualizar o status da vaga. Tente novamente.');
    }
  };

  const handleDelete = async (job: Job) => {
    if (!confirm(`Excluir a vaga "${job.titulo}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await deleteJobMutation.mutateAsync(job.id);
    } catch (err) {
      console.error('Erro ao excluir vaga:', err);
      alert('Erro ao excluir a vaga. Tente novamente.');
    }
  };

  const list = jobs ?? [];

  if (error) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-error/10">
            <AlertTriangle size={26} className="text-error" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-text-title">Erro ao carregar vagas</h2>
          <p className="text-sm text-text-secondary">Não foi possível carregar a lista de vagas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end max-md:hidden">
        <Button onClick={openCreate} className="font-semibold">
          <Plus size={18} />
          <span>Nova vaga</span>
        </Button>
      </div>
      <BotaoFlutuante icon={Plus} label="Nova vaga" onClick={openCreate} />

      {isLoading ? (
        <>
          <div className="flex flex-col gap-3 lg:hidden">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-card"
              >
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-10 w-32 rounded-[10px]" />
                </div>
              </div>
            ))}
          </div>
          <div className="hidden w-full overflow-hidden rounded-xl border border-border bg-card shadow-card animate-fade-up lg:block">
            <div className="overflow-x-auto">
              <TabelaCartoes className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  <tr>
                    <th className="px-6 py-3">Vaga</th>
                    <th className="px-6 py-3">Área</th>
                    <th className="px-6 py-3">Tipo</th>
                    <th className="px-6 py-3">Local</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Candidaturas</th>
                    <th className="px-6 py-3 text-right">Ações</th>
                  </tr>
                </thead>
              </TabelaCartoes>
              <TableSkeleton
                rows={3}
                columns={['w-40', 'w-24', 'w-20', 'w-20', 'w-16', 'w-16', 'w-16']}
              />
            </div>
          </div>
        </>
      ) : list.length > 0 ? (
        <>
          {/* Celular e tablet: um cartão por vaga; ações em botões de ícone */}
          <div className="flex flex-col gap-3 lg:hidden">
            {list.map((job) => (
              <VagaCartao
                key={job.id}
                job={job}
                onEdit={() => openEdit(job)}
                onToggleStatus={() => handleToggleStatus(job)}
                onDelete={() => handleDelete(job)}
                togglePending={updateJobMutation.isPending}
                deletePending={deleteJobMutation.isPending}
              />
            ))}
          </div>
          <div className="hidden w-full overflow-hidden rounded-xl border border-border bg-card shadow-card animate-fade-up lg:block">
            <div className="overflow-x-auto">
              <TabelaCartoes className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  <tr>
                    <th className="px-6 py-3">Vaga</th>
                    <th className="px-6 py-3">Área</th>
                    <th className="px-6 py-3">Tipo</th>
                    <th className="px-6 py-3">Local</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Candidaturas</th>
                    <th className="px-6 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {list.map((job) => (
                    <tr key={job.id} className="hover:bg-muted/30">
                      <td className="px-6 py-4">
                        <span className="font-medium text-text-title">{job.titulo}</span>
                        <span className="block text-xs text-text-hint">{job.slug}</span>
                      </td>
                      <td className="px-6 py-4 text-text-body">{job.area}</td>
                      <td className="px-6 py-4 text-text-body">{job.tipoContrato}</td>
                      <td className="px-6 py-4 text-text-body">{job.local}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            job.status === 'aberta'
                              ? 'bg-online/10 text-online'
                              : 'bg-muted text-text-secondary'
                          }`}
                        >
                          {job.status === 'aberta' ? 'Aberta' : 'Fechada'}
                        </span>
                      </td>
                      <td className="px-6 py-4 tabular-nums text-text-body">
                        {job.candidaturasCount}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEdit(job)}
                            className="text-xs"
                          >
                            <Pencil size={14} />
                            Editar
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleToggleStatus(job)}
                            disabled={updateJobMutation.isPending}
                            className="text-xs"
                          >
                            {job.status === 'aberta' ? <Lock size={14} /> : <Unlock size={14} />}
                            {job.status === 'aberta' ? 'Fechar' : 'Reabrir'}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(job)}
                            disabled={deleteJobMutation.isPending}
                            className="text-xs"
                          >
                            <Trash2 size={14} />
                            Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TabelaCartoes>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full overflow-hidden rounded-xl border border-border bg-card shadow-card animate-fade-up">
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10">
                <Briefcase size={26} className="text-primary" />
              </div>
              <p className="mb-1 text-lg font-medium text-text-title">Nenhuma vaga cadastrada</p>
              <p className="text-sm text-text-secondary">Crie a primeira vaga do site.</p>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm max-md:items-end max-md:p-0">
          <div className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-elevated max-h-[90vh] overflow-y-auto animate-fade-up max-md:max-h-[92dvh] max-md:rounded-b-none max-md:border-x-0 max-md:border-b-0 max-md:pb-[env(safe-area-inset-bottom)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card p-6">
              <h2 className="text-2xl font-semibold text-text-title">
                {editingJob ? 'Editar Vaga' : 'Nova Vaga'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeModal}
                disabled={isSaving}
                aria-label="Fechar"
                className="rounded-full"
              >
                <X size={20} />
              </Button>
            </div>

            <div className="p-6">
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                <div>
                  <label className={labelClass}>Título *</label>
                  <input
                    type="text"
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    className={inputClass}
                    placeholder="ex: Desenvolvedor(a) Flutter Pleno"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Área *</label>
                    <select
                      value={form.area}
                      onChange={(e) => setForm({ ...form, area: e.target.value })}
                      className={inputClass}
                    >
                      {JOB_AREAS.map((area) => (
                        <option key={area} value={area}>
                          {area}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Tipo de contrato *</label>
                    <input
                      type="text"
                      value={form.tipoContrato}
                      onChange={(e) => setForm({ ...form, tipoContrato: e.target.value })}
                      className={inputClass}
                      placeholder="ex: PJ · Integral"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Local *</label>
                    <input
                      type="text"
                      value={form.local}
                      onChange={(e) => setForm({ ...form, local: e.target.value })}
                      className={inputClass}
                      placeholder="ex: Remoto (Brasil)"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value as JobStatus })}
                      className={inputClass}
                    >
                      <option value="aberta">Aberta</option>
                      <option value="fechada">Fechada</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Resumo (card da vaga)</label>
                  <textarea
                    value={form.resumo}
                    onChange={(e) => setForm({ ...form, resumo: e.target.value })}
                    className={inputClass}
                    rows={2}
                    placeholder="Resumo curto exibido no card da lista de vagas"
                  />
                </div>

                <div>
                  <label className={labelClass}>Descrição (topo da página da vaga)</label>
                  <textarea
                    value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    className={inputClass}
                    rows={3}
                    placeholder="Parágrafo de apresentação da vaga"
                  />
                </div>

                <div className="border-t border-border pt-4">
                  <label className={labelClass}>Responsabilidades (uma por linha)</label>
                  <textarea
                    value={form.responsabilidades}
                    onChange={(e) => setForm({ ...form, responsabilidades: e.target.value })}
                    className={inputClass}
                    rows={4}
                    placeholder={'Desenvolver features do app\nRevisar PRs do time'}
                  />
                </div>

                <div>
                  <label className={labelClass}>Requisitos (um por linha)</label>
                  <textarea
                    value={form.requisitos}
                    onChange={(e) => setForm({ ...form, requisitos: e.target.value })}
                    className={inputClass}
                    rows={4}
                    placeholder={'Experiência com Flutter\nInglês intermediário'}
                  />
                </div>

                <div>
                  <label className={labelClass}>Processo seletivo</label>
                  <textarea
                    value={form.processo}
                    onChange={(e) => setForm({ ...form, processo: e.target.value })}
                    className={inputClass}
                    rows={2}
                    placeholder="ex: Triagem → entrevista técnica → entrevista com liderança"
                  />
                </div>

                <div className="flex items-center gap-2 border-t border-border pt-4">
                  <label className="text-sm font-medium text-text-title">Ordem</label>
                  <input
                    type="number"
                    min={0}
                    value={form.ordem || ''}
                    onChange={(e) => setForm({ ...form, ordem: parseInt(e.target.value) || 0 })}
                    className="w-20 rounded-lg border border-border bg-bg-elevated px-3 py-1.5 text-sm text-text-body transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="0"
                  />
                </div>
              </form>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-card p-6">
              <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <RefreshCw size={16} className="animate-spin" />}
                {isSaving ? 'Salvando...' : editingJob ? 'Salvar Alterações' : 'Criar Vaga'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reconstrói o payload completo a partir de uma vaga já existente (usado pelo
// toggle de status, que não passa pelo form do modal).
function jobToFormInput(job: Job): JobInput {
  return {
    titulo: job.titulo,
    area: job.area,
    tipoContrato: job.tipoContrato,
    local: job.local,
    resumo: job.resumo,
    descricao: job.descricao,
    responsabilidades: job.responsabilidades ?? [],
    requisitos: job.requisitos ?? [],
    processo: job.processo,
    status: job.status,
    ordem: job.ordem,
  };
}

const botaoIcone =
  'flex size-10 items-center justify-center rounded-[10px] border border-border text-text-secondary transition-colors active:bg-muted disabled:opacity-50';

// Cartão da vaga no celular e no tablet: título e status no topo, "área · tipo
// · local" embaixo, e na base a contagem de candidaturas com as ações em
// botões de ícone de 40px (editar, fechar/reabrir, excluir).
function VagaCartao({
  job,
  onEdit,
  onToggleStatus,
  onDelete,
  togglePending,
  deletePending,
}: {
  job: Job;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
  togglePending: boolean;
  deletePending: boolean;
}) {
  const aberta = job.status === 'aberta';
  const total = job.candidaturasCount ?? 0;

  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="flex items-start gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[15px] font-semibold leading-snug text-text-title">
            {job.titulo}
          </span>
          <span className="text-[13px] text-text-secondary">
            {[job.area, job.tipoContrato, job.local].filter(Boolean).join(' · ')}
          </span>
        </div>
        <Selo tom={aberta ? 'sucesso' : 'neutro'}>{aberta ? 'Aberta' : 'Fechada'}</Selo>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] text-text-secondary">
          {total === 0 ? (
            'Nenhuma candidatura'
          ) : (
            <>
              <span className="font-semibold tabular-nums text-text-title">{total}</span>{' '}
              {total === 1 ? 'candidatura' : 'candidaturas'}
            </>
          )}
        </span>
        <div className="flex gap-1">
          <button type="button" onClick={onEdit} aria-label="Editar vaga" className={botaoIcone}>
            <Pencil size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onToggleStatus}
            disabled={togglePending}
            aria-label={aberta ? 'Fechar vaga' : 'Reabrir vaga'}
            className={botaoIcone}
          >
            {aberta ? (
              <Lock size={16} aria-hidden="true" />
            ) : (
              <Unlock size={16} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deletePending}
            aria-label="Excluir vaga"
            className={cn(botaoIcone, 'text-error')}
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
